// FileStore — abstraction layer between the studio's "everything is a project
// folder" mental model and whatever physical storage the host environment
// provides. apps/console talks to fs.* on disk; apps/studio-open's web
// build talks to OPFS via this interface; a future Capacitor build would
// implement this against the native iOS filesystem; a future Drive build
// would implement it against Drive's REST API.
//
// All paths are slash-separated, leaf-first ("project-name/slides/01.html").
// Implementations normalize leading/trailing slashes themselves; callers
// should NOT depend on a particular casing or trailing-slash convention.

export interface FileEntry {
  name: string;
  kind: 'file' | 'directory';
  /** Bytes for files; undefined for directories. */
  size?: number;
  /** Unix epoch ms; may be undefined on hosts that don't expose it (OPFS). */
  lastModified?: number;
}

export interface FileStore {
  /** Read a UTF-8 text file. Throws if the file is missing. */
  readTextFile(path: string): Promise<string>;

  /** Read a binary file. Throws if missing. */
  readBinaryFile(path: string): Promise<Uint8Array>;

  /**
   * Write (create-or-replace) a UTF-8 text file at the given path.
   * Parent directories are created as needed.
   */
  writeTextFile(path: string, content: string): Promise<void>;

  /** Same as writeTextFile but for raw bytes. */
  writeBinaryFile(path: string, content: Uint8Array): Promise<void>;

  /**
   * Non-recursive directory listing. Returns entries directly under `path`.
   * Throws if the directory doesn't exist (use `exists` first to probe).
   */
  listDir(path: string): Promise<FileEntry[]>;

  /** Returns true iff the path exists, regardless of file/dir kind. */
  exists(path: string): Promise<boolean>;

  /**
   * Create a directory (and any missing parents) at the given path.
   * No-op if it already exists.
   */
  ensureDir(path: string): Promise<void>;

  /**
   * Delete a file. Throws if path is missing or refers to a directory.
   * For dir deletion use `deleteDir` (intentionally separate so callers
   * stop and think before recursive removes).
   */
  deleteFile(path: string): Promise<void>;

  /**
   * Recursively delete a directory and everything under it.
   * Throws if path is missing or refers to a file.
   */
  deleteDir(path: string): Promise<void>;

  /**
   * Move / rename oldPath → newPath. Both can be in different directories.
   * Implementations may copy + delete if the underlying store has no
   * rename primitive (OPFS as of 2026 is in this bucket).
   * Throws if oldPath is missing or newPath already exists.
   */
  rename(oldPath: string, newPath: string): Promise<void>;
}

/**
 * Normalize a slash-separated path into clean segments.
 *   "/foo//bar/" → ["foo", "bar"]
 *   "."          → []
 *   ""           → []
 * Throws on `..` segments — we do not let agent-driven writes climb out
 * of the project root by accident.
 */
export function splitPath(path: string): string[] {
  const segs = path
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s !== '.');
  for (const s of segs) {
    if (s === '..') {
      throw new Error(`path escape attempt: '${path}'`);
    }
  }
  return segs;
}
